import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SearchBar } from './SearchBar';
import { FilterBar } from './FilterBar';
import { PlayerTable } from './PlayerTable';
import { CoachSidebar } from '../../../components/coach/dashboard/CoachSidebar';
import { fetchUncommittedPlayers, getCoachDashboard } from '../../../features/coach/coachProfileSlice';
import { PlayerFilterModal } from '../../../components/common/PlayerFilterModal';
import { SimpleDropdown } from '../../../components/common/SimpleDropdown';

export const UncommittedTracker = () => {
  const { activeView } = useOutletContext();
  const dispatch = useDispatch();
  const { uncommittedPlayers, uncommittedPagination, uncommittedLoading, uncommittedError, followedPlayers } = useSelector((state) => state.coachProfile);

  const [position, setPosition] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [uncommittedOnly, setUncommittedOnly] = useState('uncommitted'); // Default to uncommitted
  const [selectedStatColumns, setSelectedStatColumns] = useState([]);
  const [filterActiveTab, setFilterActiveTab] = useState(null);
  const [filterRanges, setFilterRanges] = useState({}); // Store min-max filter values
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [limit, setLimit] = useState(20);
  const [showPlayerFilterModal, setShowPlayerFilterModal] = useState(false);
  const [playerFilters, setPlayerFilters] = useState({});
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [headerNameSearch, setHeaderNameSearch] = useState('');
  const [headerTeam, setHeaderTeam] = useState(null);
  const [statCategory, setStatCategory] = useState('HITTING');
  const [qualifiedPlayer, setQualifiedPlayer] = useState(false);

  // Generate year list - 2025 and 10 previous years
  const currentYear = 2025;
  const [year, setYear] = useState(2025); // Default to 2024 (2024-25 season)

  // Fetch followed players on mount to check follow status
  useEffect(() => {
    dispatch(getCoachDashboard({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Debounce search input - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    // Cleanup: cancel the timeout if search changes before 500ms
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch uncommitted players on mount and when filters change
  useEffect(() => {
    const params = {
      page: 1,
      limit,
    };
    if (statCategory) params.category = statCategory;
    {
      const statsTypeMap = { 'HITTING': 'batting', 'PITCHING': 'pitching', 'FIELDING': 'fielding', 'BASE RUNNING': 'batting' };
      const tabMap = { 'hitting': 'batting', 'pitching': 'pitching', 'fielding': 'fielding', 'base running': 'batting' };
      const resolved = statsTypeMap[statCategory] || tabMap[(filterActiveTab || '').toLowerCase()];
      if (resolved) params.statsType = resolved;
    }
    if (position && position !== 'All') params.position = position;
    if (debouncedSearch) params.name = debouncedSearch;
    if (year && year !== 'All') params.seasonYear = year;

    // Add commitmentStatus based on dropdown value
    if (uncommittedOnly) {
      params.commitmentStatus = uncommittedOnly;
    }

    // Add filter ranges for API filtering
    if (Object.keys(filterRanges).length > 0) {
      params.filters = filterRanges;
    }

    // Add sort params
    if (sortBy) {
      params.sortBy = sortBy;
      params.sortOrder = sortOrder;
    }

    // Add player filter modal params
    if (playerFilters.conference) params.conference = playerFilters.conference;
    if (playerFilters.division) params.division = playerFilters.division;
    if (playerFilters.classification) params.classYear = playerFilters.classification;
    if (playerFilters.position && !position) params.position = playerFilters.position;
    if (playerFilters.playerName && !debouncedSearch) params.name = playerFilters.playerName;
    if (playerFilters.teamId) params.teamId = playerFilters.teamId;
    if (playerFilters.region) params.region = playerFilters.region;
    if (playerFilters.state) params.state = playerFilters.state;
    if (playerFilters.handedness) params.handedness = playerFilters.handedness;
    if (playerFilters.splits) params.splits = playerFilters.splits;

    // Add multi-select position filter from table header
    if (selectedPositions.length > 0) params.position = selectedPositions.join(',');
    // Add header search params
    if (headerNameSearch) params.name = headerNameSearch;
    if (headerTeam) params.teamId = headerTeam._id || headerTeam.id;
    params.qualifiedPlayer = qualifiedPlayer;

    dispatch(fetchUncommittedPlayers(params));
  }, [dispatch, position, uncommittedOnly, debouncedSearch, year, filterRanges, sortBy, sortOrder, limit, playerFilters, selectedPositions, headerNameSearch, headerTeam, statCategory, qualifiedPlayer]);

  const handleApplyFilters = (filters) => {
    console.log('Applied filters:', filters);

    // Store selected filters for dynamic column display
    if (filters.selectedFilters && filters.selectedFilters.length > 0) {
      setSelectedStatColumns(filters.selectedFilters);
      setFilterActiveTab(filters.activeTab);
    } else {
      setSelectedStatColumns([]);
      setFilterActiveTab(null);
    }

    // Store filter ranges for API filtering (triggers refetch via useEffect)
    if (filters.filterRanges) {
      setFilterRanges(filters.filterRanges);
    } else {
      setFilterRanges({});
    }
  };

  const handlePositionChange = (newPosition) => {
    setPosition(newPosition);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > uncommittedPagination.totalPages) return;

    const params = {
      page: newPage,
      limit,
    };
    if (statCategory) params.category = statCategory;
    {
      const statsTypeMap = { 'HITTING': 'batting', 'PITCHING': 'pitching', 'FIELDING': 'fielding', 'BASE RUNNING': 'batting' };
      const tabMap = { 'hitting': 'batting', 'pitching': 'pitching', 'fielding': 'fielding', 'base running': 'batting' };
      const resolved = statsTypeMap[statCategory] || tabMap[(filterActiveTab || '').toLowerCase()];
      if (resolved) params.statsType = resolved;
    }
    if (position && position !== 'All') params.position = position;
    if (debouncedSearch) params.name = debouncedSearch;
    if (year && year !== 'All') params.seasonYear = year;

    // Add commitmentStatus based on dropdown value
    if (uncommittedOnly) {
      params.commitmentStatus = uncommittedOnly;
    }

    // Add filter ranges for API filtering
    if (Object.keys(filterRanges).length > 0) {
      params.filters = filterRanges;
    }

    // Add sort params
    if (sortBy) {
      params.sortBy = sortBy;
      params.sortOrder = sortOrder;
    }

    // Add player filter modal params
    if (playerFilters.conference) params.conference = playerFilters.conference;
    if (playerFilters.division) params.division = playerFilters.division;
    if (playerFilters.classification) params.classYear = playerFilters.classification;
    if (playerFilters.position && !position) params.position = playerFilters.position;
    if (playerFilters.playerName && !debouncedSearch) params.name = playerFilters.playerName;
    if (playerFilters.teamId) params.teamId = playerFilters.teamId;
    if (playerFilters.region) params.region = playerFilters.region;
    if (playerFilters.state) params.state = playerFilters.state;
    if (playerFilters.handedness) params.handedness = playerFilters.handedness;
    if (playerFilters.splits) params.splits = playerFilters.splits;

    // Add multi-select position filter from table header
    if (selectedPositions.length > 0) params.position = selectedPositions.join(',');
    // Add header search params
    if (headerNameSearch) params.name = headerNameSearch;
    if (headerTeam) params.teamId = headerTeam._id || headerTeam.id;
    params.qualifiedPlayer = qualifiedPlayer;

    dispatch(fetchUncommittedPlayers(params));

    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUncommittedChange = (value) => {
    setUncommittedOnly(value);
  };

  const handleSort = (backendField, direction) => {
    setSortBy(backendField);
    setSortOrder(direction);
  };

  const handleYearChange = (newYear) => {
    setYear(newYear);
  };

  const handleResetFilters = () => {
    setPosition('');
    setSearch('');
    setFilterRanges({});
    setSelectedStatColumns([]);
    setFilterActiveTab(null);
    setSortBy(null);
    setSortOrder('asc');
    setPlayerFilters({});
    setSelectedPositions([]);
    setHeaderNameSearch('');
    setHeaderTeam(null);
    setUncommittedOnly('uncommitted');
    setYear(2025);
    setStatCategory('HITTING');
    setQualifiedPlayer(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Left Sidebar */}
      <div className="lg:col-span-1 ">
        <CoachSidebar activeView={activeView} />
      </div>

      {/* Right Content Area */}
      <div className="lg:col-span-3 w-full mx-auto bg-jp-card border border-jp-line rounded-3xl shadow-sm px-4 sm:px-6 py-4 sm:py-8">
      <div className="mb-4 sm:mb-6 text-left">
        <div className="flex items-center gap-4 mb-1 sm:mb-2">
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-jp-ink">
            UNCOMMITTED TRACKER
          </div>
          <button
            type="button"
            onClick={() => setQualifiedPlayer(!qualifiedPlayer)}
            style={{ marginLeft: 'auto' }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
              qualifiedPlayer
                ? 'bg-cyan-500 border-cyan-500 text-white'
                : 'bg-transparent border-jp-line text-jp-muted hover:border-jp-accent/50 hover:text-jp-ink'
            }`}
          >
            <span className={`relative inline-flex w-9 h-5 rounded-full transition-colors ${qualifiedPlayer ? 'bg-cyan-300' : 'bg-jp-section'}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${qualifiedPlayer ? 'translate-x-4' : 'translate-x-0'}`} />
            </span>
            Qualified Players
          </button>
        </div>
        <div className="text-jp-muted text-xs sm:text-sm mb-4">
          Browse players currently available for recruitment.<br className="hidden sm:block" />
          Use filters to narrow your search and connect directly
        </div>
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-end gap-3">
          <div className="w-full sm:w-auto" style={{ minWidth: '300px', maxWidth: '400px' }}>
              <SearchBar value={search} onChange={setSearch} />
            </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-wrap">
            {/* Commitment Status Dropdown */}
            <div className="w-full sm:w-auto" style={{ minWidth: '200px' }}>
              <SimpleDropdown
                value={uncommittedOnly}
                onChange={handleUncommittedChange}
                options={[
                  { label: 'All Players', value: '' },
                  { label: 'Committed', value: 'committed' },
                  { label: 'Uncommitted', value: 'uncommitted' },
                ]}
                placeholder="Commitment Status"
                variant="form"
              />
            </div>
            {/* Category Dropdown */}
            <div className="w-full sm:w-auto" style={{ minWidth: '160px' }}>
              <SimpleDropdown
                value={statCategory}
                onChange={(val) => setStatCategory(val)}
                options={[
                  { value: '', label: 'HITTING/PITCHING' },
                  { value: 'HITTING', label: 'HITTING' },
                  { value: 'PITCHING', label: 'PITCHING' },
                ]}
                placeholder="HITTING/PITCHING"
                variant="form"
              />
            </div>
            {/* Filters button */}
            <button
              onClick={() => setShowPlayerFilterModal(true)}
              className="inline-flex items-center justify-center gap-2 text-jp-ink font-medium h-11 px-4
                         rounded-lg border border-jp-line hover:bg-jp-section transition-colors
                         w-full sm:w-auto text-sm whitespace-nowrap"
              type="button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-jp-ink">
                <path d="M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39A.998.998 0 0 0 18.95 4H5.04a1 1 0 0 0-.79 1.61z"/>
              </svg>
              <span>Filters</span>
              {Object.values(playerFilters).filter(Boolean).length > 0 && (
                <span className="bg-cyan-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {Object.values(playerFilters).filter(Boolean).length}
                </span>
              )}
            </button>
            <FilterBar
              positionValue={position}
              onPosChange={handlePositionChange}
              yearValue={year}
              onYearChange={handleYearChange}
              onAddSubtractClick={() => setShowFilterModal(!showFilterModal)}
              showFilterModal={showFilterModal}
              onCloseModal={() => setShowFilterModal(false)}
              onApplyFilters={handleApplyFilters}
              uncommittedOnly={uncommittedOnly}
              onUncommittedChange={handleUncommittedChange}
              statCategory={statCategory}
            />
          </div>
        </div>

        {/* Player Filter Modal */}
        <PlayerFilterModal
          isOpen={showPlayerFilterModal}
          onClose={() => setShowPlayerFilterModal(false)}
          onApply={(f) => setPlayerFilters(f)}
          initialFilters={playerFilters}
        />
      </div>

      <div className="rounded-lg bg-jp-card overflow-hidden">
        <PlayerTable
          data={uncommittedError ? [] : uncommittedPlayers}
          loading={uncommittedLoading}
          error={uncommittedError}
          pagination={uncommittedPagination}
          onPageChange={handlePageChange}
          limit={limit}
          onLimitChange={(val) => setLimit(val)}
          followedPlayers={followedPlayers}
          showNewSchool={uncommittedOnly === 'committed'}
          selectedStatColumns={selectedStatColumns}
          filterActiveTab={filterActiveTab}
          statCategory={statCategory}
          sortColumn={sortBy}
          sortDirection={sortOrder}
          onSort={handleSort}
          selectedPositions={selectedPositions}
          onPositionFilter={setSelectedPositions}
          nameSearch={headerNameSearch}
          onNameSearch={setHeaderNameSearch}
          selectedTeam={headerTeam}
          onTeamSelect={setHeaderTeam}
          onResetFilters={handleResetFilters}
        />
      </div>
      </div>
    </div>
  );
};
